/**
 * "New version installed" bookkeeping (#3315).
 *
 * The acknowledged version lives in localStorage, per tenant and user, not in
 * the database: a test environment's database is replaced every night with a
 * copy of production, which would reset a server-side acknowledgement.
 */

export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface VersionAck {
  /** Version the user has seen (acknowledged or first seen). */
  readonly sha: string;
  /** Version before that, if known: the baseline for "what is new". */
  readonly previousSha: string | null;
}

export type VersionNoticeDecision =
  | { readonly kind: 'none' }
  | { readonly kind: 'record-first-seen' }
  | { readonly kind: 'show'; readonly previousSha: string };

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

const SHA = /^[0-9a-f]{7,40}$/;

export function versionAckKey(tenantId: string, userId: string): string {
  return `os-buddy:version-ack:${tenantId}:${userId}`;
}

/** Same commit when one SHA is a prefix of the other (short vs full SHA). */
export function shaMatches(a: string, b: string): boolean {
  const left = a.toLowerCase();
  const right = b.toLowerCase();
  if (left.length < 7 || right.length < 7) return left === right;
  return left.startsWith(right) || right.startsWith(left);
}

/**
 * The build SHA baked into the frontend. Deploys set the version to
 * `YYYY.MM.DD+<sha7>`; a local build has no SHA.
 */
export function buildShaFromAppVersion(appVersion: string): string | null {
  const match = /\+([0-9a-f]{7,40})$/i.exec(appVersion.trim());
  return match?.[1]?.toLowerCase() ?? null;
}

/** The tab runs an older frontend than the server it talks to. */
export function isStaleTab(frontendSha: string | null, serverSha: string | null): boolean {
  if (frontendSha === null || serverSha === null) return false;
  return !shaMatches(frontendSha, serverSha);
}

export function readVersionAck(storage: KeyValueStorage, key: string): VersionAck | null {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<VersionAck>;
    if (typeof parsed.sha !== 'string' || !SHA.test(parsed.sha)) return null;
    const previousSha =
      typeof parsed.previousSha === 'string' && SHA.test(parsed.previousSha) ? parsed.previousSha : null;
    return { sha: parsed.sha, previousSha };
  } catch {
    return null;
  }
}

export function writeVersionAck(storage: KeyValueStorage, key: string, ack: VersionAck): void {
  try {
    storage.setItem(key, JSON.stringify(ack));
  } catch {
    // Storage full or blocked: the notice shows again next time, nothing breaks.
  }
}

export function decideVersionNotice(ack: VersionAck | null, currentSha: string | null): VersionNoticeDecision {
  if (currentSha === null) return { kind: 'none' };
  // First visit on this device: nothing to compare with, so no notice.
  if (ack === null) return { kind: 'record-first-seen' };
  if (shaMatches(ack.sha, currentSha)) return { kind: 'none' };
  return { kind: 'show', previousSha: ack.sha };
}

/** The acknowledgement after the user has seen `currentSha`. */
export function acknowledgeVersion(ack: VersionAck | null, currentSha: string): VersionAck {
  if (ack === null) return { sha: currentSha, previousSha: null };
  if (shaMatches(ack.sha, currentSha)) return ack;
  return { sha: currentSha, previousSha: ack.sha };
}
